import React, { useEffect, useRef, useState } from 'react';
import { useInView } from '../hooks/useInView';

const metrics = [
  { value: '70%', label: 'Less review time', color: '#00C9B1' },
  { value: '<1 day', label: 'Release cycle time', color: '#7B3FE4' },
  { value: '+18 pts', label: 'Right-First-Time uplift', color: '#F5A623' },
  { value: '3×', label: 'Reviewer throughput', color: '#4C6EF5' },
];

const kpis = [
  { icon: '▦', title: 'KPIs out of the box', desc: 'RFT, cycle time, exception rate, on-time release, reviewer throughput — by product & site.' },
  { icon: '☰', title: 'Build-your-own', desc: 'Drag-built, role-based, real-time — for every persona.', badge: 'Configurable' },
  { icon: '⧉', title: 'Custom reports', desc: 'Saved, scheduled, exported to PDF / Excel.' },
];

export default function Metrics() {
  const [ref, inView] = useInView();

  return (
    <section style={{
      padding: '120px 24px',
      background: 'linear-gradient(180deg, rgba(22,27,36,0.6), rgba(13,17,23,0.9))',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} style={{
          textAlign: 'center', marginBottom: 64,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <p style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#00C9B1', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
          }}>Metrics & BI</p>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 900, lineHeight: 1.08,
            letterSpacing: '-0.03em', color: '#fff', marginBottom: 16,
          }}>Self-serve BI on every release.</h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', maxWidth: 500, margin: '0 auto' }}>
            Out-of-the-box quality KPIs plus build-your-own dashboards — the numbers leaders report on.
          </p>
        </div>

        {/* Metric tiles */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 20, marginBottom: 56,
        }}>
          {metrics.map((m, i) => {
            const [mRef, mInView] = useInView();
            return (
              <div key={m.label} ref={mRef} style={{
                background: `linear-gradient(135deg, ${m.color}12, ${m.color}06)`,
                border: `1px solid ${m.color}25`,
                borderRadius: 16, padding: '32px 24px',
                textAlign: 'center',
                opacity: mInView ? 1 : 0,
                transform: mInView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms`,
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                  background: m.color,
                }} />
                <div style={{
                  fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: 900,
                  color: m.color, lineHeight: 1, marginBottom: 12,
                  letterSpacing: '-0.03em',
                }}>{m.value}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{m.label}</div>
              </div>
            );
          })}
        </div>

        {/* KPI grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16, marginBottom: 36,
        }}>
          {kpis.map((k, i) => {
            const [kRef, kInView] = useInView();
            return (
              <div key={k.title} ref={kRef} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14, padding: '22px',
                opacity: kInView ? 1 : 0,
                transform: kInView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms`,
              }}>
                <div style={{ fontSize: 20, marginBottom: 12 }}>{k.icon}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <h4 style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{k.title}</h4>
                  {k.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 800, background: '#F5A623', color: '#000',
                      padding: '2px 7px', borderRadius: 100, textTransform: 'uppercase',
                    }}>{k.badge}</span>
                  )}
                </div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>{k.desc}</p>
              </div>
            );
          })}
        </div>

        <div style={{
          background: 'rgba(245,166,35,0.06)',
          border: '1px solid rgba(245,166,35,0.15)',
          borderRadius: 12, padding: '14px 22px',
        }}>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
            <span style={{ color: '#F5A623', fontWeight: 700 }}>Configurable: </span>
            Dashboards, KPIs, and report schedules are configurable per site and persona — no code, no IT ticket. <em style={{ opacity: 0.6 }}>Metric tiles are illustrative outcomes, not guarantees; results vary by process and baseline.</em>
          </p>
        </div>
      </div>
    </section>
  );
}
